import { useState } from 'react'
import { useDataTableContext } from 'components/DataTable'
import useApiService from 'services/api'
import {
  bulkDeleteDealReports,
  type ListReportDealsQueryParams,
} from 'services/reports'
import lodash from 'lodash'
import confirm from 'components/dialogConfirm'
import { queryClient } from 'services/queryClient'
import { toast } from 'react-toastify'

interface BulkDealsDeleterProps {
  data: any[]
  accessorKey: string
  tableQueryKey: string[]
  filterParams: any
  useReportsEndpoint?: boolean
}

export function useBulkDealsDeleter(props: BulkDealsDeleterProps) {
  const api = useApiService()
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    state: { rowSelection, isAllRowsSelected, totalRowsInBackend },
  } = useDataTableContext()

  async function handleSubmit() {
    const selectedRows = props.data?.filter((_, i) => {
      if (isAllRowsSelected) {
        return !rowSelection?.[i]
      }
      return !!rowSelection?.[i]
    })

    const selectedDealIDs = selectedRows?.map((r: any) =>
      lodash.get(r, props.accessorKey)
    ) as number[]

    const unselectedCount = props.data?.filter((_, i) => {
      return !rowSelection?.[i]
    }).length

    const count = isAllRowsSelected
      ? totalRowsInBackend - unselectedCount
      : selectedDealIDs.length

    const confirmed = await confirm(
      `Are you sure you want to delete ${count > 1 ? `all ${count} deals` : 'this deal'}?`,
      'Delete deals',
      undefined,
      {
        confirmLabel: 'Delete',
      }
    )

    if (!confirmed) {
      return
    }

    setIsDeleting(true)
    try {
      // Use appropriate endpoint based on whether this is for reports table.
      // The reports endpoint takes `{include_ids, exclude_ids}` directly
      // (mirrors the old `buildBulkIdBody` shape) plus the active filters as
      // query params, so a filtered "select all" delete scopes to the
      // filtered set server-side instead of the whole table.
      //
      // Called as a raw fetcher (not a wrapped/auto-invalidating mutation
      // hook) on purpose: deletes reflect into the `reports/deals/` read
      // model asynchronously via CDC, so an immediate cache invalidation
      // would race the pipeline and re-show the just-deleted rows. The
      // delayed manual refetch below is the sole invalidation path — see
      // the raw `bulkDeleteDealReports` export's doc in
      // services/reports/hooks.ts, and the same CDC-race class documented
      // on `moveMutation` in DealsKanbanBoard.tsx.
      let data: { message?: string } | undefined
      if (props.useReportsEndpoint) {
        data = await bulkDeleteDealReports(
          {
            include_ids: isAllRowsSelected ? [] : selectedDealIDs,
            exclude_ids: isAllRowsSelected ? selectedDealIDs : [],
          },
          { params: props.filterParams as ListReportDealsQueryParams }
        )
      } else {
        data = await api.bulkDeleteDeals(
          selectedDealIDs,
          isAllRowsSelected,
          props.filterParams
        )
      }

      if (data?.message) {
        toast.info(
          data?.message ??
            'We are processing your request, you will be notified when it is done'
        )
      } else {
        toast.success('Deals deleted successfully')
      }

      // Wait before refetching to allow reports to process.
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Refetch both the table query and all deal card queries
      await Promise.all([
        queryClient.refetchQueries({ queryKey: props.tableQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['deals'] }),
      ])
    } finally {
      setIsDeleting(false)
    }
  }

  return { handleSubmit, isDeleting }
}
